"use client";

/**
 * Tiny client-side session module that replaced `next-auth/react` in PR 3
 * (#619). Same hook shape as NextAuth so the ~70 consumer components
 * didn't need behavioural changes — they just swap their import path.
 *
 * The session itself is hydrated server-side by `lib/session.ts` (which
 * reads the `rawiah.session` cookie + calls /api/auth/me) and passed in
 * via the SessionProvider in `components/Providers.tsx`. Updates happen
 * via full page navigation rather than NextAuth's `update()` — the
 * backend cookie is HttpOnly so the client can't mint a new session
 * locally, and that's the right boundary anyway.
 */

import { createContext, useContext } from "react";
import type { Session } from "next-auth";

type SessionStatus = "authenticated" | "unauthenticated" | "loading";

type SessionContextValue = {
  data: Session | null;
  status: SessionStatus;
};

const SessionContext = createContext<SessionContextValue>({
  data: null,
  status: "unauthenticated",
});

export function SessionProvider({
  session,
  children,
}: {
  session: Session | null;
  children: React.ReactNode;
}) {
  const value: SessionContextValue = {
    data: session,
    status: session ? "authenticated" : "unauthenticated",
  };
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue & {
  /**
   * No-op kept for backwards compatibility with code that used to call
   * NextAuth's `update()`. With the backend-driven session, fresh data
   * comes from the next server render — there's no client-side mutation
   * channel.
   */
  update: (data?: unknown) => Promise<null>;
} {
  const ctx = useContext(SessionContext);
  return {
    ...ctx,
    update: async () => null,
  };
}
